<template>
  <b-modal id="add-wardrobe-product-modal" @show="resetData">
    <template v-slot:modal-header="{ close }">
      <div class="title">ADD ITEM</div>
      <b-link class="modal-close-link float-right" right @click="close()">
        <i class="pe-7s-close close-icon"></i>
      </b-link>
    </template>

    <div class="box pt-4 pb-4">
      <div class="box-part" id="bp-left">
        <div class="partition" id="partition-register">
          <div class="partition-form">
            <b-alert
              show
              dismissible
              variant="danger"
              class="mb-5 temp-disabled-alert"
              v-if="showAlertBox"
            >
              <b>Mr Porter</b> is currently slower than usual and could take 1-2
              minutes to complete.
            </b-alert>
            <form
              class="pb-4"
              autocomplete="false"
              ref="addProductForm"
              @submit.prevent="submitAddWardrobeProduct"
            >
              <div class="custom-form-elements">
                <div class="input-group input-group-icon">
                  <b-form-input
                    :state="validProductUrlState"
                    v-model="productUrl"
                    type="text"
                    placeholder="Product URL"
                    debounce="500"
                  />
                  <div class="input-icon"><i class="pe-7s-link"></i></div>
                </div>
              </div>

              <div class="wardrobe-group-selectbox">
                <v-select
                  v-model="groupSlug"
                  class="style-chooser mb-3"
                  :class="{ 'not-empty': groupSlug }"
                  placeholder="Select Group"
                  :options="layerGroups"
                  :reduce="group => group.slug"
                  label="name"
                  required
                />
              </div>

              <div
                class="wardrobe-group-selectbox"
                v-if="requiresColourSelection"
              >
                <v-select
                  v-model="productVariant"
                  class="style-chooser mb-3"
                  :class="{ 'not-empty': productVariant }"
                  placeholder="Select Colour"
                  :options="productVariants"
                  label="colour_name"
                  required
                />
              </div>

              <div class="wardrobe-similar-checkbox">
                <label>
                  <input type="checkbox" v-model="similar" />
                  <span aria-label="This is similar to the one I own">
                    This is <span class="similar-text">similar</span> to the one
                    I own
                  </span>
                </label>
              </div>
            </form>

            <button
              type="submit"
              name="button"
              class="add-product-btn w-100 mt-4"
              v-bind:class="{ 'wardrobe-submit-error': hasError }"
              v-on:click="submitAddWardrobeProduct"
              v-html="buttonText"
              :disabled="isDisabled"
            />
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <div class="wrapper w-100" v-b-toggle.collapse-supported-sites>
        <div class="supporting-sites">
          <span>Partners</span>
          <i class="pe-7s-angle-down expand-icons"></i>
        </div>

        <b-collapse visible id="collapse-supported-sites">
          <BrandsDisplay class="mt-4 mb-4" />
        </b-collapse>
      </div>
    </template>
  </b-modal>
</template>

<script>
import BrandsDisplay from "@/components/brandsDisplay";
import MiscUtils from "@/utils/MiscUtils";
import { mapActions, mapGetters } from "vuex";
import { bus } from "@/main";

export default {
  name: "AddWardrobeProductModal",

  components: {
    BrandsDisplay
  },

  created() {
    this.validateURL = MiscUtils.validateURL;
  },

  data() {
    return {
      groupSlug: null,
      hasError: false,
      layerGroups: this.$constants.LAYER_GROUPS,
      productUrl: null,
      productVariant: null,
      similar: false,
      sourceSiteName: null,
      sourceSites: this.$constants.SOURCE_SITES,
      tempDisabledSourceSites: [],
      validUrl: null
    };
  },

  methods: {
    ...mapActions([
      "addWardrobeProduct",
      "eagerCreateProduct",
      "restEagerCreateProductResponse"
    ]),

    resetData() {
      this.groupSlug = null;
      this.hasError = false;
      this.productUrl = null;
      this.productVariant = null;
      this.similar = false;
      this.sourceSiteName = null;
      this.validUrl = null;
      this.restEagerCreateProductResponse();
    },

    submitAddWardrobeProduct() {
      const payload = {
        wardrobeId: this.currentWardrobe.id,
        productId: this.determineProductId,
        productUrl: this.determineProductUrl,
        similar: this.similar,
        group: this.groupSlug
      };

      bus.$emit("addingWardrobeProduct", true);

      this.addWardrobeProduct(payload).then(() => {
        bus.$emit("addingWardrobeProduct", false);
      });

      this.$nextTick(() => {
        this.$bvModal.hide("add-wardrobe-product-modal");
      });
    },

    setSourceSiteName(newProductUrl) {
      this.sourceSiteName = null;
      if (!newProductUrl) return;

      this.sourceSites.forEach((source, index) => {
        if (newProductUrl.indexOf(source.slug) !== -1) {
          this.sourceSiteName = source.name;
        }
      });
    },

    setValidUrl(newProductUrl) {
      this.validUrl = newProductUrl ? this.validateURL(newProductUrl) : null;
    }
  },

  computed: {
    ...mapGetters([
      "currentUser",
      "currentWardrobe",
      "eagerCreatProductResponse"
    ]),

    buttonText() {
      if (this.validProductUrlState && this.groupSlug) {
        this.hasError = false;
        return `get product from <span class="site-name">${this.sourceSiteName}</span>`;
      } else if (
        this.validUrl &&
        this.sourceSiteName &&
        this.temporarilyDisabled
      ) {
        this.hasError = true;
        return `<b>${this.sourceSiteName}</b> is temporarily disabled`;
      } else if (this.validProductUrlState && !this.groupSlug) {
        this.hasError = true;
        return 'please select <span class="site-name">group</span>';
      } else if (this.validUrl && !!!this.sourceSiteName) {
        this.hasError = true;
        return "Sorry, retailer not available :(";
      } else if (
        (this.productUrl && !!!this.validUrl) ||
        (!this.productUrl && this.groupSlug)
      ) {
        this.hasError = true;
        return 'please enter valid <span class="site-name">product url</span>';
      } else {
        this.hasError = false;
        return "Get product";
      }
    },

    temporarilyDisabled() {
      return this.tempDisabledSourceSites.includes(this.sourceSiteName);
    },

    isDisabled() {
      return (
        !!!this.validProductUrlState ||
        !this.groupSlug ||
        !!!this.eagerCreatProductResponse ||
        (this.requiresColourSelection && !!!this.productVariant)
      );
    },

    showAlertBox() {
      return (
        this.tempDisabledSourceSites.length > 0 ||
        this.sourceSiteName === "Mr Porter"
      );
    },

    validProductUrlState() {
      return this.productUrl
        ? this.validUrl && !!this.sourceSiteName && !this.temporarilyDisabled
        : null;
    },

    productVariants() {
      return this.eagerCreatProductResponse
        ? this.eagerCreatProductResponse.product_variants
        : [];
    },

    requiresColourSelection() {
      return (
        this.eagerCreatProductResponse &&
        this.eagerCreatProductResponse.requires_colour_selection
      );
    },

    determineProductUrl() {
      return this.productVariant
        ? this.productVariant["external_url"]
        : this.productUrl;
    },

    determineProductId() {
      return this.productVariant
        ? this.productVariant["product_id"]
        : this.eagerCreatProductResponse["product_id"];
    }
  },

  watch: {
    productUrl: function(newVal, oldVal) {
      this.setValidUrl(newVal);
      this.setSourceSiteName(newVal);
    },

    validProductUrlState: function(newVal, oldVal) {
      if (!!newVal) {
        this.eagerCreateProduct(this.productUrl);
      }
    }
  }
};
</script>
<style lang="scss">
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_custom_form.scss";
@import "~@/styles/_modal_add_item_btn.scss";
@import "~@/styles/_selectbox.scss";
@import "./styles/_checkbox.scss";

.similar-text {
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/wardrobe/AddWardrobeProductModal.vue